import { Box, Typography } from '@mui/material';
import FieldsBuilder from '../../../Components/FormBuilder/FieldsBuilder';
import { REGEX } from '../../../Helpers/constant';
import AttachmentIcon from '@mui/icons-material/Attachment';
import { useStyles } from '../styles';
import moment from 'moment';
import { useEffect, useState } from 'react';
import { getFundCategories } from '../../../Utils/apiService';
import KenLoader from '../../../GlobalComponents/KenLoader';
import { useFormContext } from 'react-hook-form';

const CreateCampaign: React.FunctionComponent<any> = () => {
  const classes = useStyles();
  const { watch, setValue } = useFormContext();
  const startDateChange = watch('startDate');
  const endDateChange = watch('endDate');
  const base64Image = watch('base64Image');

  const [categories, setCategories] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [coverPreview, setCoverPreview] = useState<string>('');

  useEffect(() => {
    setLoading(true);
    getFundCategories()
      .then((res) => {
        if (res.success === 'true') {
          const categories = res.data.data?.map((items: any) => {
            return {
              label: items?.Name,
              value: items?.Id,
            };
          });
          setCategories(categories);
          setLoading(false);
        }
      })
      .catch((err) => {
        console.error(err);
        setLoading(false);
      });
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        const result = reader.result as string;
        const base64Only = result.split(',')[1];
        setValue('base64Image', base64Only, { shouldValidate: true });
        setValue('imageName', file.name, { shouldValidate: true });
        setCoverPreview(result);
      };
      reader.readAsDataURL(file);
    }
  };

  return (
    <>
      {loading && <KenLoader />}
      <Box
        component={'div'}
        sx={{
          height: { xs: 'auto', md: '100%' },
          overflow: { xs: 'visible', md: 'visible' },
          paddingRight: 1,
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
        }}
        className="d-flex flex-column align-items-start w-100 gap-3 scrollbar-cusgray"
      >
        <FieldsBuilder
          fields={{
            fieldStyle: {
              width: '100%',
              fontSize: '14px',
            },
            type: 'text',
            name: 'campaignTitle',
            required: true,
            label: 'Title of the fundraising campaign',
            validation: {
              required: 'This field is required',
              maxLength: {
                value: 80,
                message: 'This field should not be greater than 80 characters',
              },
            },
            placeholder: 'Add title',
            labelClassName: classes.label,
          }}
        />
        <FieldsBuilder
          fields={{
            fieldStyle: {
              width: '100%',
              fontSize: '14px',
            },
            type: 'select',
            options: categories,
            name: 'fundCategory',
            label: 'Choose category',
            height: "46px",
            required: true,
            validation: {
              required: 'This field is required',
            },
            placeholder: 'Select',
            labelClassName: classes.label,
          }}
        />
        <FieldsBuilder
          fields={{
            fieldStyle: {
              width: '100%',
              fontSize: '14px',
            },
            type: 'textarea',
            multiline: true,
            rows: 4,
            name: 'description',
            label: 'Description',
            required: true,
            validation: {
              required: 'This field is required',
              maxLength: {
                value: 300,
                message: 'This field should not be greater than 300 characters',
              },
            },
            placeholder: 'Please provide a detailed explanation of why the funds are needed',
            labelClassName: classes.label,
          }}
        />
        <FieldsBuilder
          fields={{
            fieldStyle: {
              width: '100%',
              fontSize: '14px',
            },
            type: 'number',
            name: 'goalAmount',
            label: 'Mention your fundraising goal',
            required: true,
            validation: {
              required: 'This field is required',
              min: {
                value: 1,
                message: 'Minimum amount should be ₹1',
              },
            },
            placeholder: '00',
            InputProps: {
              endAdornment: (
                <Typography component={'h6'} color={'#8C8E94'}>
                  ₹
                </Typography>
              ),
            },
            labelClassName: classes.label,
          }}
        />
        <FieldsBuilder
          fields={{
            fieldStyle: {
              width: '100%',
              fontSize: '14px',
            },
            type: 'text',
            name: 'fundraiserLink',
            label: 'Paste your external fundraiser link here (optional)',
            validation: {
              pattern: {
                value: REGEX.HTTPS,
                message: 'Please enter a valid URL',
              },
            },
            placeholder: 'Paste link',
            InputProps: {
              endAdornment: <AttachmentIcon sx={{ color: '#8C8E94' }} />,
            },
            labelClassName: classes.label,
          }}
        />
        <Box sx={{ width: '100%', mt: 2 }}>
          <Typography className={classes.label} fontWeight={500} fontSize="14px" mb={1}>
            Cover image (optional)
          </Typography>
          <Box
            sx={{
              border: '1px dashed #B2B3B7',
              borderRadius: '8px',
              padding: 2,
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              background: '#FAFAFA',
              cursor: 'pointer',
              transition: 'border-color 0.2s',
              '&:hover': {
                borderColor: '#006BB7',
              },
              width: '100%',
              minHeight: 60,
            }}
            onClick={() => {
              // Focus the file input when the box is clicked
              document.getElementById('cover-image-upload')?.click();
            }}
          >
            <input
              id="cover-image-upload"
              type="file"
              accept="image/*"
              onChange={handleFileChange}
              style={{ display: 'none' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography fontSize="13px" color="#8C8E94">
                Click or drag an image here to upload
              </Typography>
              <Typography fontSize="12px" color="#B2B3B7">
                (JPG, PNG, or GIF)
              </Typography>
            </Box>
            {(coverPreview || base64Image) && (
              <Box
                sx={{
                  ml: 2,
                  border: '1px solid #E0E0E0',
                  borderRadius: '6px',
                  overflow: 'hidden',
                  width: 60,
                  height: 40,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  background: '#fff',
                }}
              >
                <img
                  src={coverPreview || `data:image/*;base64,${base64Image}`}
                  alt="Cover Preview"
                  style={{ maxWidth: '100%', maxHeight: '100%' }}
                />
              </Box>
            )}
          </Box>
        </Box>
        <div className="d-flex flex-column align-items-start gap-1">
          <Typography color={'#373A45'} fontSize={'16px'} fontWeight={'500'}>
            Set a timeline <span style={{ color: 'red' }}>*</span>
          </Typography>
          <div className="gap-2 w-100 d-block d-md-flex d-md-align-items-center">
            <FieldsBuilder
              fields={{
                fieldStyle: {
                  width: '100%',
                  fontSize: '14px',
                },
                type: 'date',
                name: 'startDate',
                validation: {
                  required: 'This field is required',
                  validate: (value: string) => {
                    return moment(value).isBefore(endDateChange) || 'Please choose a date after start date';
                  },
                },
                minDate: moment().format('YYYY-MM-DD'),
                placeholder: 'Start date',
              }}
            />
            <FieldsBuilder
              fields={{
                fieldStyle: {
                  width: '100%',
                  fontSize: '14px',
                },
                type: 'date',
                name: 'endDate',
                validation: {
                  required: 'This field is required',
                },
                disabled: !startDateChange,
                minDate: moment(startDateChange).format('YYYY-MM-DD'),
                placeholder: 'End date',
              }}
            />
          </div>
        </div>
      </Box>
    </>
  );
};

export default CreateCampaign;
